import { useEffect, useId, useState } from 'react';
import type { FormEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { postRequest } from '../../../../../Helper/ApiClient';
import { useListingRequest } from '../../../../../Context/ListingRequestContext';
import { useListingRequestEnabled } from '../../../../../Hooks/useListingRequestEnabled';
import type { ListingRequestSource } from '../../../../../../Interface/ListingRequest';
import type { BoothPanelProps } from '../panels';
import { hasHighlightsTab } from '../panels';
import styles from './ContactPanel.module.scss?module';

interface BoothLink {
  label: string;
  url: string;
  icon: IconDefinition;
}

type SendState = 'idle' | 'sent' | 'failed';

const MESSAGE_MAX_LENGTH = 2000;
/** A booth lists the products of one brand, like the product lists that use this source. */
const LISTING_REQUEST_SOURCE: ListingRequestSource = 'category';
const NUMBER_FORMAT = new Intl.NumberFormat('en-GB');

/**
 * Contact: the brand description for booths without the Highlights tab, a message to the brand
 * through the user's Naskor contact (support chat), the listing request and the brand's website
 * and social links.
 */
export default function ContactPanel({
  exhibitor,
  tab,
  onBadge,
  track,
}: BoothPanelProps) {
  const { openListingRequest } = useListingRequest();
  const listingRequestEnabled = useListingRequestEnabled();
  const sendMutation = postRequest('/api/onlineshop/support-chat/messages');
  const messageHintId = useId();
  const [message, setMessage] = useState('');
  const [sendState, setSendState] = useState<SendState>('idle');

  useEffect(() => {
    onBadge(tab, null);
  }, [tab, onBadge]);

  const links = boothLinks(exhibitor);
  const trimmedMessage = message.trim();
  // Booths with Highlights & new show the description there, under "Why {brand}".
  const description = hasHighlightsTab(exhibitor)
    ? ''
    : exhibitor.description?.trim() ?? '';

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (trimmedMessage === '' || sendMutation.isPending) {
      return;
    }
    const formData = new FormData();
    formData.append('body', '[VTS · ' + exhibitor.name + '] ' + trimmedMessage);
    setSendState('idle');
    sendMutation.mutate(formData, {
      onSuccess: () => {
        setMessage('');
        setSendState('sent');
        track('contact_sent');
      },
      onError: () => {
        setSendState('failed');
      },
    });
  };

  const handleListingRequest = () => {
    openListingRequest(LISTING_REQUEST_SOURCE);
    track('listing_request_opened');
  };

  return (
    <div className={styles.panel}>
      {description !== '' && (
        <section className={[styles.card, styles.aboutCard].join(' ')}>
          <h2 className={styles.sectionLabel}>About {exhibitor.name}</h2>
          <p className={styles.aboutText}>{description}</p>
        </section>
      )}

      <section className={[styles.card, styles.messageCard].join(' ')}>
        <h2 className={styles.sectionLabel}>Message the brand</h2>
        <p id={messageHintId} className={styles.cardText}>
          Your message goes to your Naskor contact, who forwards it to{' '}
          {exhibitor.name}.
        </p>
        <form className={styles.messageForm} onSubmit={handleSubmit}>
          <textarea
            className={styles.textarea}
            rows={4}
            maxLength={MESSAGE_MAX_LENGTH}
            value={message}
            aria-label={'Message to ' + exhibitor.name}
            aria-describedby={messageHintId}
            onChange={(event) => {
              setMessage(event.target.value);
              if (sendState !== 'idle') {
                setSendState('idle');
              }
            }}
          />
          <div className={styles.formFooter}>
            <span className={styles.counter}>
              {NUMBER_FORMAT.format(message.length)} /{' '}
              {NUMBER_FORMAT.format(MESSAGE_MAX_LENGTH)}
            </span>
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={trimmedMessage === '' || sendMutation.isPending}
            >
              Send
            </button>
          </div>
          <p className={styles.successText} role="status">
            {sendState === 'sent'
              ? 'Sent. Your Naskor contact will get back to you.'
              : ''}
          </p>
          {sendState === 'failed' && (
            <p className={styles.errorText} role="alert">
              Could not send your message. Please try again.
            </p>
          )}
        </form>
      </section>

      {listingRequestEnabled && (
        <section className={styles.card}>
          <h2 className={styles.sectionLabel}>Listing request</h2>
          <p className={styles.cardText}>
            Missing an item from {exhibitor.name} in our range? Tell us.
          </p>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleListingRequest}
          >
            Open listing request
          </button>
        </section>
      )}

      {links.length > 0 && (
        <section className={styles.card}>
          <h2 className={styles.sectionLabel}>Links</h2>
          <ul className={styles.linkList}>
            {links.map((link) => (
              <li key={link.label}>
                <a
                  className={styles.secondaryButton}
                  href={link.url}
                  target="_blank"
                  rel="noopener"
                >
                  <FontAwesomeIcon icon={link.icon} aria-hidden="true" />
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** The brand's website and social links that are set, in a fixed order. */
function boothLinks(exhibitor: BoothPanelProps['exhibitor']): BoothLink[] {
  const candidates: BoothLink[] = [
    { label: 'Website', url: exhibitor.website_url, icon: faGlobe },
    { label: 'Instagram', url: exhibitor.instagram_url, icon: faInstagram },
    { label: 'Facebook', url: exhibitor.facebook_url, icon: faFacebook },
    { label: 'YouTube', url: exhibitor.youtube_url, icon: faYoutube },
    { label: 'X', url: exhibitor.twitter_url, icon: faXTwitter },
  ];

  return candidates
    .map((link) => ({ ...link, url: absoluteUrl(link.url) }))
    .filter((link) => link.url !== '');
}

/** Trims the URL and adds https:// when the scheme is missing; empty stays empty. */
function absoluteUrl(url?: string | null): string {
  const trimmed = (url ?? '').trim();
  if (trimmed === '') {
    return '';
  }

  return /^https?:\/\//i.test(trimmed) ? trimmed : 'https://' + trimmed;
}
